import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { colors } from '../tokens/colors'
import { Text } from './Text'

type Option = {
  /**
   * The value of the option.
   */
  value: string
  /**
   * The label to display for the option.
   */
  label: string
}

type Props = {
  /**
   * An array of options to be displayed in the select dropdown.
   */
  options: Option[]
  /**
   * The current selected value of the SingleSelect component.
   */
  value: string
  /**
   * if the dropdown should be anchored to the right or left side of the select.
   * @default left
   */
  dropdownAnchor?: 'left' | 'right'
  /**
   * Callback function called when the selection is changed.
   * @param value - The new selected value.
   */
  onChange: (value: string) => void
}

/**
 * A component that replicates the behavior of the HTML select/option element.
 */
export const SingleSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  dropdownAnchor = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedOption, setHighlightedOption] = useState<string | null>(
    null
  )
  const selectRef = useRef<HTMLDivElement>(null)

  /**
   * Event handler for when an option is clicked.
   * @param optionValue - The value of the clicked option.
   */
  const handleOptionClick = (optionValue: string) => {
    setIsOpen(false)
    onChange(optionValue)
  }

  /**
   * Event handler for keydown events, used for keyboard navigation and selection.
   * @param event - The keyboard event.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.code) {
      case 'ArrowDown':
        event.preventDefault()
        navigateOptions(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        navigateOptions(-1)
        break
      case 'Enter':
        if (isOpen) {
          setIsOpen(false)
          if (highlightedOption) {
            handleOptionClick(highlightedOption)
          }
        } else {
          setIsOpen(true)
        }
        break
      case 'Space':
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
      default:
        break
    }
  }

  /**
   * Navigates through the options, looping through if necessary and setting the new highlighted option.
   * @param step - The number of steps to navigate.
   */
  const navigateOptions = (step: number) => {
    if (!isOpen) {
      setIsOpen(true)
      return
    }

    const currentIndex = options.findIndex(
      (option) => option.value === highlightedOption
    )
    const newIndex = (currentIndex + step + options.length) % options.length
    setHighlightedOption(options[newIndex].value)
  }

  // get the label for a given option value
  const getLabelForValue = (optionValue: string) => {
    const selectedOption = options.find(
      (option) => option.value === optionValue
    )
    return selectedOption ? selectedOption.label : ''
  }

  // set the highlighted option to the current value when the select is opened
  useEffect(() => {
    if (isOpen) {
      setHighlightedOption(value)
    }
  }, [isOpen, value])

  // close the select when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // focus the select when it is opened
  useEffect(() => {
    if (isOpen && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isOpen])

  return (
    <SelectWrapper
      isOpen={isOpen}
      ref={selectRef}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <SelectedOption
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-label='Select an option'
        aria-controls='select-options'
      >
        <Text variant='p3'>
          {value ? getLabelForValue(value) : 'Select an option'}
        </Text>
        {isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
      </SelectedOption>
      {isOpen && (
        <OptionsList
          id='select-options'
          role='listbox'
          dropdownAnchor={dropdownAnchor}
          aria-activedescendant={highlightedOption || ''}
        >
          {options.map((option) => (
            <OptionItem
              id={option.value}
              key={option.value}
              isSelected={option.value === highlightedOption}
              isActive={option.value === value}
              onClick={() => handleOptionClick(option.value)}
              role='option'
              aria-label={option.label}
              aria-selected={option.value === value}
            >
              <Text variant='p3'>{option.label}</Text>
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </SelectWrapper>
  )
}
const SelectWrapper = styled.div<{
  isOpen?: boolean
}>`
  position: relative;
  display: inline-block;
  border-radius: 4px;
  background: ${colors.interactive.secondaryEnabled};
  font-size: 14px;
  min-width: 160px;
  box-sizing: border-box;
  &:focus {
    box-shadow: ${({ isOpen }) =>
      isOpen
        ? ``
        : `0px 0px 0px 1px ${colors.interactive.borderFocusOrange} inset,
      0px 0px 0px 3px ${colors.interactive.shadowFocusOrange}`};
    outline: none;
  }
  &:hover {
    box-shadow: 0px 0px 0px 1px ${colors.interactive.borderEnabledHoverGray}
      inset;
    background: ${colors.interactive.secondaryHoverFocus};
  }
  &:active {
    box-shadow: 0px 0px 0px 1px ${colors.interactive.borderActiveDarkBlue} inset;
  }
`

const SelectedOption = styled.div<{
  isOpen?: boolean
}>`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 2px 2px 6px;
  border-radius: 4px;
  height: 20px;
  color: ${colors.interactive.primaryEnabled};
  box-shadow: ${({ isOpen }) =>
    isOpen
      ? `0px 0px 0px 1px ${colors.interactive.borderActiveDarkBlue}
        inset;`
      : ''};
`

const OptionsList = styled.div<{ dropdownAnchor: string }>`
  position: absolute;
  top: 32px;
  left: ${({ dropdownAnchor }) =>
    dropdownAnchor === 'left' ? '0' : 'initial'};
  right: ${({ dropdownAnchor }) =>
    dropdownAnchor === 'right' ? '0' : 'initial'};
  z-index: 1;
  width: max-content;
  background: #fff;
  border-radius: 4px;
  padding: 4px 1px;
  background: var(--background-secondary-background, #fff);
  box-shadow:
    0px 0px 0px 1px ${colors.interactive.borderActiveDarkBlue} inset,
    0px 0px 0px 2px rgba(0, 50, 99, 0.2);
`

const OptionItem = styled.div<{ isSelected: boolean; isActive: boolean }>`
  cursor: pointer;
  border-bottom: 1px solid ${colors.background.tertiaryBackground};
  background: ${colors.background.secondaryBackground};
  padding: 4px 8px;
  color: ${colors.interactive.primaryEnabled};
  height: 20px;
  background-color: ${({ isSelected, isActive }) =>
    isActive || isSelected
      ? colors.interactive.tableHoverFocus
      : 'transparent'};

  ${({ isActive }) =>
    isActive ? `color: ${colors.interactive.primaryEnabled}` : ''};
  box-shadow: ${({ isSelected }) =>
    isSelected
      ? `0px 0px 0px 2px ${colors.interactive.borderFocusOrange} inset;`
      : 'transparent'};

  &:hover {
    transition: background-color 0.1s ease-in;
    background: ${colors.interactive.tableHoverFocus};
  }
`
